import React from "react";
import useSWR from "swr";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { DatePicker } from "@/components/app/DatePicker";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { DownloadSimple } from "@phosphor-icons/react";
import { fetcher, fmtN, todayISO, downloadXlsx } from "./common";

const Th = ({ children, right, ...rest }) => <th {...rest} className={`px-3 py-2 ${right ? "text-right" : "text-left"}`}>{children}</th>;
const Td = ({ children, right, mono, ...rest }) => <td {...rest} className={`px-3 py-1.5 ${right ? "text-right" : ""} ${mono ? "font-mono text-xs" : ""}`}>{children}</td>;

function PeriodPicker({ from, to, setFrom, setTo }) {
  return (
    <div className="flex flex-wrap gap-3 items-end mb-4">
      <div><Label>Du</Label><DatePicker value={from} onChange={setFrom} testid="report-from" /></div>
      <div><Label>Au</Label><DatePicker value={to} onChange={setTo} testid="report-to" /></div>
    </div>
  );
}

export function BalanceTab({ file }) {
  const yearStart = `${new Date().getFullYear()}-01-01`;
  const [from, setFrom] = React.useState(yearStart);
  const [to, setTo] = React.useState(todayISO());
  const [drafts, setDrafts] = React.useState(false);
  const { data, isLoading } = useSWR(`/accounting/files/${file.id}/balance?from_d=${from}&to_d=${to}&include_drafts=${drafts}`, fetcher);
  const rows = data?.rows || [];
  const tot = rows.reduce((t, r) => ({
    od: t.od + r.open_d, oc: t.oc + r.open_c, md: t.md + r.mvt_d, mc: t.mc + r.mvt_c, sd: t.sd + r.solde_d, sc: t.sc + r.solde_c,
  }), { od: 0, oc: 0, md: 0, mc: 0, sd: 0, sc: 0 });

  return (
    <div>
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div className="flex flex-wrap gap-3 items-end">
          <PeriodPicker from={from} to={to} setFrom={setFrom} setTo={setTo} />
          <label className="flex items-center gap-2 text-xs text-zinc-600 pb-2">
            <input type="checkbox" checked={drafts} onChange={(e) => setDrafts(e.target.checked)} data-testid="balance-include-drafts" />
            Inclure les brouillards
          </label>
        </div>
        <Button variant="outline" onClick={() => downloadXlsx(`/accounting/files/${file.id}/balance?from_d=${from}&to_d=${to}&include_drafts=${drafts}&format=xlsx`, `balance_6_colonnes_${file.name}.xlsx`)} data-testid="balance-export">
          <DownloadSimple size={16} className="mr-1" /> Excel
        </Button>
      </div>
      <div className="border border-zinc-200 bg-white rounded-md overflow-auto">
        <table className="w-full text-sm whitespace-nowrap">
          <thead className="bg-zinc-50 border-b border-zinc-200">
            <tr className="text-[10px] uppercase tracking-[0.18em] text-zinc-500">
              <Th>Compte</Th><Th>Intitulé</Th><Th right>SI Débit</Th><Th right>SI Crédit</Th><Th right>Mvt Débit</Th><Th right>Mvt Crédit</Th><Th right>Solde Débiteur</Th><Th right>Solde Créditeur</Th>
            </tr>
          </thead>
          <tbody className="divide-y divide-zinc-100">
            {isLoading && <tr><td colSpan={8} className="p-6 text-zinc-500">Calcul…</td></tr>}
            {!isLoading && rows.length === 0 && <tr><td colSpan={8} className="p-6 text-zinc-500">Aucun mouvement sur la période.</td></tr>}
            {rows.map((r) => (
              <tr key={r.code} className="hover:bg-zinc-50" data-testid={`balance-row-${r.code}`}>
                <Td mono>{r.code}</Td><Td>{r.name}</Td>
                <Td right>{r.open_d ? fmtN(r.open_d) : ""}</Td><Td right>{r.open_c ? fmtN(r.open_c) : ""}</Td>
                <Td right>{r.mvt_d ? fmtN(r.mvt_d) : ""}</Td><Td right>{r.mvt_c ? fmtN(r.mvt_c) : ""}</Td>
                <Td right>{r.solde_d ? fmtN(r.solde_d) : ""}</Td><Td right>{r.solde_c ? fmtN(r.solde_c) : ""}</Td>
              </tr>
            ))}
          </tbody>
          {rows.length > 0 && (
            <tfoot className="bg-zinc-50 border-t border-zinc-200 font-medium">
              <tr>
                <Td colSpan={2}>Totaux</Td>
                <Td right>{fmtN(tot.od)}</Td><Td right>{fmtN(tot.oc)}</Td>
                <Td right>{fmtN(tot.md)}</Td><Td right>{fmtN(tot.mc)}</Td>
                <Td right>{fmtN(tot.sd)}</Td><Td right>{fmtN(tot.sc)}</Td>
              </tr>
            </tfoot>
          )}
        </table>
      </div>
    </div>
  );
}

export function LedgerTab({ file }) {
  const [prefix, setPrefix] = React.useState("");
  const [from, setFrom] = React.useState(`${new Date().getFullYear()}-01-01`);
  const [to, setTo] = React.useState(todayISO());
  const { data, isLoading } = useSWR(`/accounting/files/${file.id}/ledger?prefix=${prefix}&from_d=${from}&to_d=${to}`, fetcher);
  const rows = data?.rows || [];

  return (
    <div>
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div className="flex flex-wrap gap-3 items-end">
          <div>
            <Label>Compte (préfixe)</Label>
            <Input className="w-36" placeholder="Ex : 411, 521…" value={prefix} onChange={(e) => setPrefix(e.target.value)} data-testid="ledger-prefix" />
          </div>
          <PeriodPicker from={from} to={to} setFrom={setFrom} setTo={setTo} />
        </div>
        <Button variant="outline" onClick={() => downloadXlsx(`/accounting/files/${file.id}/ledger?prefix=${prefix}&from_d=${from}&to_d=${to}&format=xlsx`, `grand_livre_${prefix || "general"}_${file.name}.xlsx`)} data-testid="ledger-export">
          <DownloadSimple size={16} className="mr-1" /> Excel
        </Button>
      </div>
      <div className="border border-zinc-200 bg-white rounded-md overflow-auto max-h-[65vh]">
        <table className="w-full text-sm whitespace-nowrap">
          <thead className="bg-zinc-50 border-b border-zinc-200 sticky top-0">
            <tr className="text-[10px] uppercase tracking-[0.18em] text-zinc-500">
              <Th>Date</Th><Th>Journal</Th><Th>Pièce</Th><Th>Libellé</Th><Th>Compte</Th><Th>Tiers</Th><Th right>Débit</Th><Th right>Crédit</Th><Th right>Solde cumulé</Th>
            </tr>
          </thead>
          <tbody className="divide-y divide-zinc-100">
            {isLoading && <tr><td colSpan={9} className="p-6 text-zinc-500">Chargement…</td></tr>}
            {!isLoading && rows.length === 0 && <tr><td colSpan={9} className="p-6 text-zinc-500">Aucune écriture.</td></tr>}
            {rows.map((r) => (
              <tr key={`${r.piece}-${r.account}-${r.date}`} className="hover:bg-zinc-50">
                <Td>{r.date}</Td><Td>{r.journal}</Td><Td mono>{r.piece}</Td>
                <Td><span className="truncate inline-block max-w-[240px] align-middle">{r.label}</span></Td>
                <Td mono>{r.account}</Td><Td>{r.tiers || ""}</Td>
                <Td right>{r.debit ? fmtN(r.debit) : ""}</Td><Td right>{r.credit ? fmtN(r.credit) : ""}</Td>
                <Td right>{fmtN(r.running)}</Td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export function TiersTab({ file }) {
  const [prefix, setPrefix] = React.useState("411");
  const [asOf, setAsOf] = React.useState(todayISO());
  const [drafts, setDrafts] = React.useState(false);
  const [inclLett, setInclLett] = React.useState(false);
  const { data: bal } = useSWR(`/accounting/files/${file.id}/tiers?prefix=${prefix}&to_d=${asOf}&include_drafts=${drafts}`, fetcher);
  const { data: aged } = useSWR(`/accounting/files/${file.id}/aged?prefix=${prefix}&as_of=${asOf}&include_drafts=${drafts}&include_lettered=${inclLett}`, fetcher);
  const balRows = bal?.rows || [];
  const agedRows = aged?.rows || [];

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-end gap-3">
        <div>
          <Label>Tiers</Label>
          <Select value={prefix} onValueChange={setPrefix}>
            <SelectTrigger className="w-48" data-testid="tiers-prefix"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="411">Clients (411)</SelectItem>
              <SelectItem value="401">Fournisseurs (401)</SelectItem>
              <SelectItem value="421">Personnel (421)</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div><Label>Situation au</Label><DatePicker value={asOf} onChange={setAsOf} testid="tiers-asof" /></div>
        <label className="flex items-center gap-2 text-xs text-zinc-600 pb-2">
          <input type="checkbox" checked={drafts} onChange={(e) => setDrafts(e.target.checked)} data-testid="tiers-include-drafts" />
          Inclure les brouillards
        </label>
        <label className="flex items-center gap-2 text-xs text-zinc-600 pb-2">
          <input type="checkbox" checked={inclLett} onChange={(e) => setInclLett(e.target.checked)} data-testid="tiers-include-lettered" />
          Inclure les lignes lettrées (balance âgée)
        </label>
        <Button variant="outline" onClick={() => downloadXlsx(`/accounting/files/${file.id}/tiers?prefix=${prefix}&to_d=${asOf}&include_drafts=${drafts}&format=xlsx`, `balance_tiers_${prefix}.xlsx`)} data-testid="tiers-export">
          <DownloadSimple size={16} className="mr-1" /> Balance Excel
        </Button>
        <Button variant="outline" onClick={() => downloadXlsx(`/accounting/files/${file.id}/aged?prefix=${prefix}&as_of=${asOf}&include_drafts=${drafts}&format=xlsx`, `balance_agee_${prefix}.xlsx`)} data-testid="aged-export">
          <DownloadSimple size={16} className="mr-1" /> Âgée Excel
        </Button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="border border-zinc-200 bg-white rounded-md overflow-auto">
          <div className="px-4 py-2 border-b border-zinc-200 text-[10px] uppercase tracking-[0.22em] text-zinc-500">Balance des tiers</div>
          <table className="w-full text-sm">
            <thead className="bg-zinc-50 border-b border-zinc-200">
              <tr className="text-[10px] uppercase tracking-[0.18em] text-zinc-500"><Th>Compte</Th><Th>Intitulé</Th><Th right>Débiteur</Th><Th right>Créditeur</Th></tr>
            </thead>
            <tbody className="divide-y divide-zinc-100">
              {balRows.length === 0 && <tr><td colSpan={4} className="p-6 text-zinc-500">Aucun solde.</td></tr>}
              {balRows.map((r) => (
                <tr key={r.code}><Td mono>{r.code}</Td><Td>{r.name}</Td><Td right>{r.solde_d ? fmtN(r.solde_d) : ""}</Td><Td right>{r.solde_c ? fmtN(r.solde_c) : ""}</Td></tr>
              ))}
            </tbody>
          </table>
        </div>

        <div className="border border-zinc-200 bg-white rounded-md overflow-auto">
          <div className="px-4 py-2 border-b border-zinc-200 text-[10px] uppercase tracking-[0.22em] text-zinc-500">
            Balance âgée <span className="normal-case tracking-normal">({aged?.note || "âge par écriture"})</span>
          </div>
          <table className="w-full text-sm whitespace-nowrap">
            <thead className="bg-zinc-50 border-b border-zinc-200">
              <tr className="text-[10px] uppercase tracking-[0.18em] text-zinc-500"><Th>Compte</Th><Th right>0-30 j</Th><Th right>31-60 j</Th><Th right>61-90 j</Th><Th right>&gt;90 j</Th><Th right>Total</Th></tr>
            </thead>
            <tbody className="divide-y divide-zinc-100">
              {agedRows.length === 0 && <tr><td colSpan={6} className="p-6 text-zinc-500">Aucune donnée.</td></tr>}
              {agedRows.map((r) => (
                <tr key={r.code} data-testid={`aged-row-${r.code}`}>
                  <Td mono>{r.code} — {r.name}</Td>
                  <Td right>{r.b0_30 ? fmtN(r.b0_30) : ""}</Td><Td right>{r.b31_60 ? fmtN(r.b31_60) : ""}</Td>
                  <Td right>{r.b61_90 ? fmtN(r.b61_90) : ""}</Td><Td right>{r.b90 ? fmtN(r.b90) : ""}</Td>
                  <Td right>{fmtN(r.total)}</Td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
